import { Search, X, type LucideIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

export interface CommandItem {
  label: string;
  description?: string;
  group: string;
  icon: LucideIcon;
  action: () => void;
  keywords?: string;
}

export function CommandPalette({
  open,
  onClose,
  items,
}: {
  open: boolean;
  onClose: () => void;
  items: CommandItem[];
}) {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    const frame = window.requestAnimationFrame(() => inputRef.current?.focus());
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  const matches = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return items;
    return items.filter((item) =>
      `${item.label} ${item.description || ''} ${item.group} ${item.keywords || ''}`
        .toLowerCase()
        .includes(normalized),
    );
  }, [items, query]);

  if (!open) return null;

  const groups = [...new Set(matches.map((item) => item.group))];
  return (
    <div
      className="fixed inset-0 z-[120] flex items-start justify-center bg-black/35 px-4 pt-[12vh] backdrop-blur-sm"
      role="presentation"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-label="Dexter command menu"
        className="atlas-enter w-full max-w-2xl overflow-hidden rounded-2xl border border-atlas-border bg-atlas-elevated shadow-atlas-float"
      >
        <div className="flex items-center gap-3 border-b border-atlas-border px-4">
          <Search className="h-5 w-5 text-atlas-muted" />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search pages, projects, and actions…"
            className="h-14 min-w-0 flex-1 bg-transparent text-sm text-atlas-ink placeholder:text-atlas-muted focus:ring-0 focus:ring-offset-0"
          />
          <button onClick={onClose} className="atlas-icon-button" aria-label="Close command menu">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="max-h-[55vh] overflow-y-auto p-2">
          {groups.map((group) => (
            <div key={group} className="py-1">
              <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-atlas-muted">
                {group}
              </div>
              {matches.filter((item) => item.group === group).map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={`${group}-${item.label}`}
                    onClick={() => {
                      item.action();
                      onClose();
                    }}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition hover:bg-atlas-subtle"
                  >
                    <span className="grid h-9 w-9 place-items-center rounded-lg border border-atlas-border bg-atlas-surface text-atlas-muted">
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-atlas-ink">{item.label}</span>
                      {item.description && <span className="mt-0.5 block truncate text-xs text-atlas-muted">{item.description}</span>}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
          {!matches.length && (
            <div className="px-4 py-12 text-center">
              <Search className="mx-auto h-6 w-6 text-atlas-muted" />
              <p className="mt-3 text-sm font-medium text-atlas-ink">No results found</p>
              <p className="mt-1 text-xs text-atlas-muted">Try a project name or navigation section.</p>
            </div>
          )}
        </div>
        <footer className="flex items-center justify-between border-t border-atlas-border bg-atlas-subtle/60 px-4 py-2 text-[11px] text-atlas-muted">
          <span>Navigate Dexter without leaving your current workspace</span>
          <span className="rounded border border-atlas-border bg-atlas-surface px-1.5 py-0.5 font-mono">esc</span>
        </footer>
      </section>
    </div>
  );
}
